import { Button } from "@/components/ui/button";
import { MessageCircle, LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Crisp } from "crisp-sdk-web";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export interface SOSButtonConfig {
  // Styling
  gradientClasses: string;
  hoverGradientClasses: string;
  
  // Icon (optional, defaults to MessageCircle)
  icon?: LucideIcon;
  
  // Translation keys (full namespace-prefixed like "mindcotine:actions.sosButton")
  translations: {
    buttonLabel: string;
    buttonLabelFallback: string;
    buttonHelp: string;
    buttonHelpFallback: string;
    toastTitle: string;
    toastTitleFallback: string;
    toastDescription: string;
    toastDescriptionFallback: string;
  };
  
  // Analytics
  analyticsEvent: string;
}

interface SOSButtonProps {
  config: SOSButtonConfig;
  className?: string;
}

export function SOSButton({ config, className }: SOSButtonProps) {
  const { t } = useTranslation();
  const { trackFeatureUsage } = useAnalytics();
  const { toast } = useToast();

  const Icon = config.icon || MessageCircle;

  const handleClick = () => {
    trackFeatureUsage(config.analyticsEvent, {
      action: t('common.sosbutton.open_support_chat', 'Open Support Chat'),
    });
    
    Crisp.chat.open();
    toast({
      title: t(config.translations.toastTitle, config.translations.toastTitleFallback),
      description: t(config.translations.toastDescription, config.translations.toastDescriptionFallback),
    });
  };

  return (
    <Button
      onClick={handleClick}
      className={cn(
        "relative overflow-hidden min-h-[140px] sm:min-h-[160px] text-white border-0 rounded-xl flex flex-col items-center justify-end p-4 gap-2 group transition-all duration-300",
        config.gradientClasses,
        config.hoverGradientClasses,
        className
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
      <div className="relative z-10 flex flex-col items-start w-full mt-auto">
        <div className="mb-2 p-2 rounded-full bg-white/20 w-fit">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="font-bold text-sm sm:text-lg leading-tight text-left">
          {t(config.translations.buttonLabel, config.translations.buttonLabelFallback)}
        </span>
        <span className="text-white/70 text-[10px] sm:text-xs text-left">
          {t(config.translations.buttonHelp, config.translations.buttonHelpFallback)}
        </span>
      </div>
    </Button>
  );
}
